import { useAccounting } from "@/contexts/AccountingContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VoucherList } from "@/components/accounting/VoucherList";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { FileText, BarChart3, IndianRupee, ArrowRight, ShieldCheck } from "lucide-react";
import { GSTINValidatorCard } from "@/components/accounting/GSTINValidator";

const GSTPage = () => {
  const { vouchers } = useAccounting();
  const navigate = useNavigate();

  const salesVouchers = vouchers.filter((v) => v.type === "Sales" && v.status === "Approved");
  const purchaseVouchers = vouchers.filter((v) => v.type === "Purchase" && v.status === "Approved");

  const totalOutput = salesVouchers.reduce((s, v) => s + (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0), 0);
  const totalInput = purchaseVouchers.reduce((s, v) => s + (v.cgst || 0) + (v.sgst || 0) + (v.igst || 0), 0);
  const netPayable = totalOutput - totalInput;

  const totalCGST = salesVouchers.reduce((s, v) => s + (v.cgst || 0), 0);
  const totalSGST = salesVouchers.reduce((s, v) => s + (v.sgst || 0), 0);
  const totalIGST = salesVouchers.reduce((s, v) => s + (v.igst || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">GST</h1>
          <p className="text-sm text-muted-foreground">GST overview, compliance, and tax breakup</p>
        </div>
        <Button onClick={() => navigate("/dashboard/gst-reports")} className="bg-accent text-accent-foreground hover:bg-accent/90 gap-2">
          <BarChart3 className="h-4 w-4" /> GST Reports <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-5 rounded-lg bg-card border border-border shadow-card">
          <div className="text-sm text-muted-foreground">Output Tax (Sales)</div>
          <div className="text-2xl font-bold font-display text-foreground">₹{totalOutput.toLocaleString("en-IN")}</div>
          <div className="text-xs text-muted-foreground mt-1">
            CGST: ₹{totalCGST.toLocaleString("en-IN")} | SGST: ₹{totalSGST.toLocaleString("en-IN")} | IGST: ₹{totalIGST.toLocaleString("en-IN")}
          </div>
        </div>
        <div className="p-5 rounded-lg bg-card border border-border shadow-card">
          <div className="text-sm text-muted-foreground">Input Tax Credit (Purchase)</div>
          <div className="text-2xl font-bold font-display text-foreground">₹{totalInput.toLocaleString("en-IN")}</div>
          <div className="text-xs text-muted-foreground mt-1">{purchaseVouchers.length} purchase vouchers</div>
        </div>
        <div className="p-5 rounded-lg bg-card border border-border shadow-card">
          <div className="text-sm text-muted-foreground">
            {netPayable > 0 ? "Net GST Payable" : netPayable < 0 ? "Excess ITC (Carried Forward)" : "GST Balanced (Nil)"}
          </div>
          <div className={`text-2xl font-bold font-display ${netPayable > 0 ? "text-destructive" : netPayable < 0 ? "text-success" : "text-foreground"}`}>
            ₹{Math.abs(netPayable).toLocaleString("en-IN")}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {netPayable > 0 ? "Cash liability due for GSTR-3B" : netPayable < 0 ? "Excess credit available for future periods (Cash payable: ₹0)" : "No tax liability due"}
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "GSTR-1", desc: "Outward supplies", tab: "gstr1" },
          { label: "GSTR-3B", desc: "Summary return", tab: "gstr3b" },
          { label: "Sales Register", desc: "GST sales", tab: "sales-register" },
          { label: "ITC Report", desc: "Input credits", tab: "itc" },
        ].map((item) => (
          <button
            key={item.tab}
            onClick={() => navigate("/dashboard/gst-reports")}
            className="p-4 rounded-lg bg-card border border-border shadow-card hover:shadow-elevated transition-shadow text-left group"
          >
            <div className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors">{item.label}</div>
            <div className="text-xs text-muted-foreground">{item.desc}</div>
          </button>
        ))}
      </div>

      {/* GSTIN Validator */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-primary" /> GSTIN Validator
          </CardTitle>
          <CardDescription>Verify any GSTIN and auto-fetch business details from the GST portal</CardDescription>
        </CardHeader>
        <CardContent>
          <GSTINValidatorCard />
        </CardContent>
      </Card>

      {/* Voucher Tabs */}
      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales">Sales (Output Tax)</TabsTrigger>
          <TabsTrigger value="purchase">Purchases (Input Tax)</TabsTrigger>
        </TabsList>
        <TabsContent value="sales">
          <VoucherList type="Sales" title="GST on Sales" description="Output tax from approved sales vouchers" />
        </TabsContent>
        <TabsContent value="purchase">
          <VoucherList type="Purchase" title="GST on Purchases" description="Input tax credit from approved purchase vouchers" />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default GSTPage;
